import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { deleteTicketRiskComment, getTicketRiskComments, postTicketRiskComment } from '~/services/SupportService';

import type { TicketRiskComment, TicketRiskCommentsResult, TicketRiskReport } from '~/types/ticketRisk';

import { ticketRiskQueryKeys } from './useTicketRiskReport';

/** Mirrors svc-support's RECENT_COMMENT_WINDOW_MINUTES — the badge counts comments this recent. */
const RECENT_COMMENT_WINDOW_MS = 60 * 60 * 1000;

/** Keeps the grid's comment counts and "last by" in step with a thread that was just changed. */
export function patchReportCommentCount(
  report: TicketRiskReport | undefined,
  result: TicketRiskCommentsResult,
  now: number = Date.now(),
): TicketRiskReport | undefined {
  if (!report) {
    return report;
  }

  return {
    ...report,
    tickets: report.tickets.map((ticket) => {
      if (ticket.ticketId !== result.ticketId) {
        return ticket;
      }
      const last = result.comments[result.comments.length - 1];
      const cutoff = now - RECENT_COMMENT_WINDOW_MS;

      return {
        ...ticket,
        commentCount: result.comments.length,
        recentCommentCount: result.comments.filter((comment) => new Date(comment.createdAt).getTime() >= cutoff).length,
        lastComment: last ? { authorName: last.authorName, createdAt: last.createdAt } : undefined,
      };
    }),
  };
}

/** One ticket's comment thread — fetched lazily, since only the detail dialog shows it. */
export function useGetTicketRiskComments(ticketId: string | null) {
  return useQuery({
    queryKey: ticketRiskQueryKeys.commentThread(ticketId ?? ''),
    queryFn: async ({ signal }) => (await getTicketRiskComments(ticketId as string, { signal })).comments,
    enabled: !!ticketId,
    refetchOnWindowFocus: false,
  });
}

function useApplyCommentsResult() {
  const queryClient = useQueryClient();

  return (result: TicketRiskCommentsResult) => {
    queryClient.setQueryData<Array<TicketRiskComment>>(
      ticketRiskQueryKeys.commentThread(result.ticketId),
      result.comments,
    );
    queryClient.setQueriesData<TicketRiskReport>({ queryKey: ticketRiskQueryKeys.reports() }, (report) =>
      patchReportCommentCount(report, result),
    );
  };
}

export function useAddTicketRiskComment() {
  const applyResult = useApplyCommentsResult();

  return useMutation({
    mutationFn: ({ ticketId, body }: { ticketId: string; body: string }) => postTicketRiskComment(ticketId, body),
    onSuccess: applyResult,
  });
}

export function useDeleteTicketRiskComment() {
  const applyResult = useApplyCommentsResult();

  return useMutation({
    mutationFn: ({ ticketId, commentId }: { ticketId: string; commentId: string }) =>
      deleteTicketRiskComment(ticketId, commentId),
    onSuccess: applyResult,
  });
}
